import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useOutsideAlerter } from '@/src/hooks/useOutsideClick';
import { VerifiedData } from '@/src/services/getVerifiedData';
import style from './style.module.scss';

const VERIFY_TYPES = [
  {
    id: 'telegram',
    labelKey: 'verify_type_telegram',
    defaultLabel: 'Telegram ID',
    placeholder: '@some_telegram_name',
  },
  {
    id: 'email',
    labelKey: 'verify_type_email',
    defaultLabel: 'Email address',
    placeholder: 'email@example.com',
  },
  {
    id: 'website',
    labelKey: 'verify_type_website',
    defaultLabel: 'Website link',
    placeholder: 'https://example.com',
  },
  {
    id: 'discord',
    labelKey: 'verify_type_discord',
    defaultLabel: 'Discord ID',
    placeholder: 'username#1234',
  },
  { id: 'x', labelKey: 'verify_type_x', defaultLabel: 'X account', placeholder: '@username' },
  {
    id: 'tiktok',
    labelKey: 'verify_type_tiktok',
    defaultLabel: 'TikTok',
    placeholder: '@username',
  },
  {
    id: 'instagram',
    labelKey: 'verify_type_instagram',
    defaultLabel: 'Instagram',
    placeholder: '@username',
  },
  {
    id: 'threads',
    labelKey: 'verify_type_threads',
    defaultLabel: 'Threads',
    placeholder: '@username',
  },
  {
    id: 'facebook',
    labelKey: 'verify_type_facebook',
    defaultLabel: 'Facebook',
    placeholder: 'https://facebook.com/...',
  },
  {
    id: 'reddit',
    labelKey: 'verify_type_reddit',
    defaultLabel: 'Reddit',
    placeholder: 'u/username',
  },
];

const normalize = (val: string) => val.trim().toLowerCase();
const normalizeUrl = (val: string) => normalize(val).replace(/\/+$/, '').replace(/\?.*$/, '');
const stripUrlToHost = (val: string) =>
  val
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/\?.*$/, '');
const isUrl = (val: string) => /^https?:\/\//i.test(val) || /^www\./i.test(val);
const hasBadProtocol = (val: string) => /^[a-z][a-z0-9+\-.]*:\/\//i.test(val) && !isUrl(val);
const extractUsernameFromUrl = (url: string): string =>
  url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^[^/]+\//, '')
    .replace(/^@/, '')
    .replace(/\?.*$/, '')
    .replace(/\/+$/, '');

const normalizeTigerComPath = (hostAndPath: string) => {
  const match = hostAndPath.match(/^(tiger\.com)(\/.*)?$/i);
  if (!match) return hostAndPath;
  const path = (match[2] || '/').replace(/^\/(ru|ua)(\/|$)/i, '$2');
  return match[1] + (path === '/' ? '' : path);
};

const verify = (type: string, value: string, verifiedData: VerifiedData): boolean => {
  const trimmed = normalize(value);
  if (!trimmed) return false;
  if (hasBadProtocol(trimmed)) return false;

  const list = verifiedData[type] || [];

  if (type === 'website') {
    const inputNormalized = normalizeUrl(trimmed);
    let inputFormatted = inputNormalized.replace(/^https?:\/\//, '').replace(/^www\./, '');
    if (/^tiger\.com(\/|$)/i.test(inputFormatted))
      inputFormatted = normalizeTigerComPath(inputFormatted);

    return list.some((item) => {
      const itemNormalized = normalizeUrl(item);
      let itemFormatted = itemNormalized.replace(/^https?:\/\//, '').replace(/^www\./, '');
      if (/^tiger\.com(\/|$)/i.test(itemFormatted))
        itemFormatted = normalizeTigerComPath(itemFormatted);

      return inputFormatted === itemFormatted;
    });
  }

  if (
    type === 'telegram' ||
    type === 'discord' ||
    type === 'x' ||
    type === 'instagram' ||
    type === 'tiktok' ||
    type === 'threads' ||
    type === 'facebook' ||
    type === 'reddit'
  ) {
    const inputIsUrl = isUrl(trimmed);
    const withAt = trimmed.startsWith('@') ? trimmed : '@' + trimmed;
    const withoutAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    return list.some((item) => {
      const n = normalize(item);
      if (n === trimmed || n === withAt || n === withoutAt) return true;
      if (inputIsUrl && isUrl(n)) {
        return normalizeUrl(n) === normalizeUrl(trimmed);
      }
      if (inputIsUrl && !isUrl(n)) {
        const listUsername = n.startsWith('@') ? n.slice(1) : n;
        return extractUsernameFromUrl(trimmed) === listUsername;
      }
      if (!inputIsUrl && isUrl(n)) {
        return withoutAt === extractUsernameFromUrl(n);
      }
      return false;
    });
  }

  return list.some((item) => {
    const n = normalize(item);
    if (n === trimmed) return true;
    if (isUrl(trimmed) || isUrl(n)) {
      return stripUrlToHost(normalizeUrl(n)) === stripUrlToHost(normalizeUrl(trimmed));
    }
    return false;
  });
};

const EmailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 8V17.75C21 18.7165 20.2165 19.5 19.25 19.5H4.75C3.7835 19.5 3 18.7165 3 17.75V8L11.4686 13.1476C11.7978 13.3474 12.2022 13.3474 12.5314 13.1476L21 8Z"
      fill="currentColor"
    />
    <path
      d="M21 6.244V6.25C21 6.25 21 6.25 21 6.25L12 11.5L3 6.25V6.25C3.00001 5.2835 3.7835 4.5 4.75 4.5H19.25C20.2165 4.5 21 5.2835 21 6.244Z"
      fill="currentColor"
    />
  </svg>
);

const LinkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.9 12C3.9 10.29 5.29 8.9 7 8.9H11V7H7C4.24 7 2 9.24 2 12C2 14.76 4.24 17 7 17H11V15.1H7C5.29 15.1 3.9 13.71 3.9 12ZM8 13H16V11H8V13ZM17 7H13V8.9H17C18.71 8.9 20.1 10.29 20.1 12C20.1 13.71 18.71 15.1 17 15.1H13V17H17C19.76 17 22 14.76 22 12C22 9.24 19.76 7 17 7Z"
      fill="currentColor"
    />
  </svg>
);

const DiscordIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.27 5.33C17.94 4.71 16.5 4.26 15 4C14.89 4.19 14.76 4.46 14.67 4.67C13.08 4.43 11.5 4.43 9.93 4.67C9.84 4.46 9.71 4.19 9.59 4C8.09 4.26 6.65 4.71 5.32 5.34C2.47 9.62 1.73 13.8 2.1 17.93C3.89 19.26 5.63 20.08 7.33 20.63C7.62 20.23 7.88 19.81 8.1 19.36C7.66 19.19 7.24 18.99 6.84 18.75C6.94 18.68 7.04 18.6 7.13 18.52C10.9 20.27 15.08 20.27 18.81 18.52C18.91 18.6 19.01 18.68 19.1 18.75C18.7 18.99 18.28 19.2 17.84 19.36C18.06 19.81 18.32 20.23 18.61 20.63C20.32 20.08 22.06 19.26 23.84 17.93C24.28 13.16 23.06 9.02 19.27 5.33ZM8.68 15.44C7.69 15.44 6.88 14.54 6.88 13.44C6.88 12.34 7.67 11.44 8.68 11.44C9.69 11.44 10.5 12.34 10.48 13.44C10.48 14.54 9.69 15.44 8.68 15.44ZM15.32 15.44C14.33 15.44 13.52 14.54 13.52 13.44C13.52 12.34 14.31 11.44 15.32 11.44C16.33 11.44 17.14 12.34 17.12 13.44C17.12 14.54 16.33 15.44 15.32 15.44Z"
      fill="currentColor"
    />
  </svg>
);

const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.244 2.25H21.552L14.325 10.51L22.827 21.75H16.17L10.956 14.933L4.99 21.75H1.68L9.41 12.915L1.254 2.25H8.08L12.793 8.481L18.244 2.25ZM17.083 19.77H18.916L7.084 4.126H5.117L17.083 19.77Z"
      fill="currentColor"
    />
  </svg>
);

const TelegramIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.665 3.717L2.935 10.554C1.725 11.04 1.732 11.715 2.713 12.016L7.265 13.436L17.797 6.791C18.295 6.488 18.75 6.651 18.376 6.983L9.843 14.684H9.841L9.843 14.685L9.529 19.377C9.989 19.377 10.192 19.166 10.45 18.917L12.661 16.767L17.26 20.164C18.108 20.631 18.717 20.391 18.928 19.379L21.947 5.151C22.256 3.912 21.474 3.351 20.665 3.717Z"
      fill="currentColor"
    />
  </svg>
);

const TikTokIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.3 0 .59.05.86.13V9.01a6.27 6.27 0 00-.86-.06 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.48a8.18 8.18 0 004.76 1.52V7.56a4.83 4.83 0 01-1-.87z"
      fill="currentColor"
    />
  </svg>
);

const InstagramIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2m-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 011.25 1.25A1.25 1.25 0 0117.25 8 1.25 1.25 0 0116 6.75a1.25 1.25 0 011.25-1.25M12 7a5 5 0 015 5 5 5 0 01-5 5 5 5 0 01-5-5 5 5 0 015-5m0 2a3 3 0 00-3 3 3 3 0 003 3 3 3 0 003-3 3 3 0 00-3-3z"
      fill="currentColor"
    />
  </svg>
);

const ThreadsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.186 2C14.74 2 16.738 2.806 18.12 4.386C19.398 5.844 20.098 7.89 20.182 10.47L20.186 10.64H17.862C17.762 8.656 17.222 7.148 16.296 6.104C15.35 5.036 13.97 4.48 12.186 4.48C10.144 4.48 8.616 5.22 7.618 6.662C6.652 8.058 6.16 10.082 6.16 12.7C6.16 15.318 6.652 17.342 7.618 18.738C8.616 20.18 10.144 20.92 12.186 20.92C13.694 20.92 14.918 20.47 15.82 19.586C16.734 18.69 17.236 17.466 17.236 16C17.236 14.862 16.908 13.964 16.276 13.34C15.666 12.738 14.844 12.42 13.84 12.42C13.118 12.42 12.5 12.612 12.018 12.988C11.558 13.346 11.286 13.82 11.234 14.394L11.226 14.52H8.878C8.93 13.166 9.434 12.068 10.35 11.278C11.256 10.496 12.432 10.1 13.84 10.1C15.576 10.1 16.982 10.646 18.016 11.716C19.028 12.764 19.546 14.188 19.546 15.95C19.546 18.154 18.814 19.914 17.38 21.178C15.976 22.416 14.208 23 12.186 23C9.412 23 7.268 22.014 5.808 20.098C4.386 18.232 3.66 15.716 3.66 12.7C3.66 9.684 4.386 7.168 5.808 5.302C7.268 3.386 9.412 2.4 12.186 2.4V2Z"
      fill="currentColor"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22 12C22 6.48 17.52 2 12 2S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
      fill="currentColor"
    />
  </svg>
);

const RedditIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.5 15.41C14.58 15.5 14.58 15.69 14.5 15.78C13.77 16.5 12.41 16.56 12 16.56C11.59 16.56 10.23 16.5 9.5 15.78C9.42 15.69 9.42 15.5 9.5 15.41C9.59 15.33 9.78 15.33 9.86 15.41C10.36 15.91 11.41 16.09 12 16.09C12.59 16.09 13.64 15.91 14.14 15.41C14.22 15.33 14.42 15.33 14.5 15.41M10.38 13.87C10.38 13.27 9.89 12.77 9.29 12.77C8.69 12.77 8.2 13.27 8.2 13.87C8.2 14.47 8.69 14.97 9.29 14.97C9.89 14.97 10.38 14.47 10.38 13.87M15.81 13.87C15.81 13.27 15.31 12.77 14.71 12.77C14.11 12.77 13.62 13.27 13.62 13.87C13.62 14.47 14.11 14.97 14.71 14.97C15.31 14.97 15.81 14.47 15.81 13.87M22 12C22 17.52 17.52 22 12 22S2 17.52 2 12 6.48 2 12 2 22 6.48 22 12M18.67 13.67C18.56 13 18.11 12.44 17.5 12.16C17.53 11.95 17.55 11.72 17.55 11.5C17.55 9.27 15.08 7.47 12 7.47C8.92 7.47 6.45 9.27 6.45 11.5C6.45 11.72 6.47 11.95 6.5 12.16C5.89 12.44 5.44 13 5.33 13.67C5.15 14.73 5.88 15.72 6.94 15.91C7.07 15.93 7.2 15.94 7.33 15.93C8.22 17.19 9.97 18 12 18C14.03 18 15.78 17.19 16.67 15.93C16.8 15.94 16.93 15.93 17.06 15.91C18.12 15.72 18.85 14.73 18.67 13.67Z"
      fill="currentColor"
    />
  </svg>
);

const TYPE_ICONS: Record<string, React.ReactNode> = {
  email: <EmailIcon />,
  website: <LinkIcon />,
  discord: <DiscordIcon />,
  x: <XIcon />,
  telegram: <TelegramIcon />,
  tiktok: <TikTokIcon />,
  instagram: <InstagramIcon />,
  threads: <ThreadsIcon />,
  facebook: <FacebookIcon />,
  reddit: <RedditIcon />,
};

const ArrowDownIcon = () => (
  <svg viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.41.59L6 5.17 10.59.59 12 2 6 8 0 2 1.41.59z" fill="currentColor" />
  </svg>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#34C759" />
    <path
      d="M6 10L9 13L14 7"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#FF3B30" />
    <path d="M7 7L13 13M13 7L7 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const WarnIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="15" viewBox="0 0 22 15" fill="none">
    <path
      d="M10.725 12C11.975 12 13.0375 11.5625 13.9125 10.6875C14.7875 9.8125 15.225 8.75 15.225 7.5C15.225 6.25 14.7875 5.1875 13.9125 4.3125C13.0375 3.4375 11.975 3 10.725 3C9.475 3 8.4125 3.4375 7.5375 4.3125C6.6625 5.1875 6.225 6.25 6.225 7.5C6.225 8.75 6.6625 9.8125 7.5375 10.6875C8.4125 11.5625 9.475 12 10.725 12ZM10.725 10.2C9.975 10.2 9.3375 9.9375 8.8125 9.4125C8.2875 8.8875 8.025 8.25 8.025 7.5C8.025 6.75 8.2875 6.1125 8.8125 5.5875C9.3375 5.0625 9.975 4.8 10.725 4.8C11.475 4.8 12.1125 5.0625 12.6375 5.5875C13.1625 6.1125 13.425 6.75 13.425 7.5C13.425 8.25 13.1625 8.8875 12.6375 9.4125C12.1125 9.9375 11.475 10.2 10.725 10.2ZM10.725 15C8.49167 15 6.45417 14.4 4.6125 13.2C2.77083 12 1.31667 10.4167 0.25 8.45C0.166667 8.3 0.104167 8.14583 0.0625 7.9875C0.0208333 7.82917 0 7.66667 0 7.5C0 7.33333 0.0208333 7.17083 0.0625 7.0125C0.104167 6.85417 0.166667 6.7 0.25 6.55C1.31667 4.58333 2.77083 3 4.6125 1.8C6.45417 0.6 8.49167 0 10.725 0C12.9583 0 14.9958 0.6 16.8375 1.8C18.6792 3 20.1333 4.58333 21.2 6.55C21.2833 6.7 21.3458 6.85417 21.3875 7.0125C21.4292 7.17083 21.45 7.33333 21.45 7.5C21.45 7.66667 21.4292 7.82917 21.3875 7.9875C21.3458 8.14583 21.2833 8.3 21.2 8.45C20.1333 10.4167 18.6792 12 16.8375 13.2C14.9958 14.4 12.9583 15 10.725 15Z"
      fill="#89859D"
    />
  </svg>
);

type ResultState = 'idle' | 'success' | 'error' | 'unavailable';

const VerifyPage = ({ verifiedData }: { verifiedData: VerifiedData }) => {
  const { t } = useTranslation();
  const isDataUnavailable = Object.keys(verifiedData).length === 0;
  const visibleVerifyTypes = useMemo(
    () =>
      isDataUnavailable
        ? VERIFY_TYPES
        : VERIFY_TYPES.filter((type) => verifiedData[type.id]?.length > 0),
    [isDataUnavailable, verifiedData]
  );

  const [selectedType, setSelectedType] = useState(VERIFY_TYPES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState<ResultState>('idle');
  const [checkedValue, setCheckedValue] = useState('');
  const wrapperRef = useRef(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollRatio, setScrollRatio] = useState(0);

  useOutsideAlerter(wrapperRef, () => setIsDropdownOpen(false));

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    setScrollRatio(maxScroll > 0 ? el.scrollTop / maxScroll : 0);
  }, []);

  const handleTypeSelect = (type: typeof VERIFY_TYPES[number]) => {
    setSelectedType(type);
    setIsDropdownOpen(false);
    setInputValue('');
    setResult('idle');
  };

  const handleCheck = () => {
    if (!inputValue.trim()) return;
    if (isDataUnavailable) {
      setResult('unavailable');
      return;
    }
    const isValid = verify(selectedType.id, inputValue, verifiedData);
    setResult(isValid ? 'success' : 'error');
    setCheckedValue(inputValue.trim());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (result !== 'idle') setResult('idle');
  };

  return (
    <div className={style.main}>
      <div className={style.blobContainer}>
        <div className={style.blobTopLeft} />
        <div className={style.blobBottomRight} />
      </div>
      <div className={style.content}>
        <div className={style.mainContainer}>
          <div className={style.title}>{t('verify_title', 'Trust, but verify')}</div>
          <div className={style.textBlock}>
            <div className={style.subtitle}>
              {t(
                'verify_subtitle',
                'Protect yourself from phishing and scammers — make sure you are communicating with an official Tiger.com representative'
              )}
            </div>
            <div className={style.description}>
              {t(
                'verify_description',
                'Enter a link to a website, email address, or a link to your chosen channel'
              )}
            </div>
          </div>
          <div className={style.inputRow}>
            <div
              ref={wrapperRef}
              className={clsx(style.inputWrapper, isDropdownOpen && style.inputWrapperOpen)}
            >
              <div
                className={style.typeSelector}
                onClick={() => {
                  const opening = !isDropdownOpen;
                  setIsDropdownOpen(opening);
                  if (opening) {
                    setScrollRatio(0);
                    setTimeout(() => {
                      if (listRef.current) listRef.current.scrollTop = 0;
                    }, 0);
                  }
                }}
              >
                <div className={style.typeSelectorIcon}>{TYPE_ICONS[selectedType.id]}</div>
                <div className={style.typeSelectorLabel}>
                  <span>{t(selectedType.labelKey, selectedType.defaultLabel)}</span>
                </div>
                <div
                  className={clsx(
                    style.typeSelectorArrow,
                    isDropdownOpen && style.typeSelectorArrowOpen
                  )}
                >
                  <ArrowDownIcon />
                </div>
              </div>
              <input
                className={style.input}
                type="text"
                value={inputValue}
                onFocus={() => setIsDropdownOpen(false)}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCheck();
                }}
                placeholder={selectedType.placeholder}
              />
              {isDropdownOpen && (
                <div className={style.typeDropdown}>
                  <div ref={listRef} className={style.typeDropdownList} onScroll={handleScroll}>
                    {visibleVerifyTypes.map((type) => (
                      <div
                        key={type.id}
                        className={clsx(
                          style.typeOption,
                          selectedType.id === type.id && style.typeOptionActive
                        )}
                        onClick={() => handleTypeSelect(type)}
                      >
                        <div className={style.typeOptionIcon}>{TYPE_ICONS[type.id]}</div>
                        <span>{t(type.labelKey, type.defaultLabel)}</span>
                      </div>
                    ))}
                  </div>
                  <div className={style.scrollTrack}>
                    <div
                      className={style.scrollThumb}
                      style={{ top: `${scrollRatio * (100 - 35)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <button className={style.submitButton} type="button" onClick={handleCheck}>
              <span>{t('verify_button', 'Check representative')}</span>
            </button>
            {result === 'success' && (
              <div className={style.resultRow}>
                <div className={style.resultSuccess}>
                  <CheckIcon />
                  <span>
                    {t('verify_result_official', 'Official Tiger.com contact')} – {checkedValue}
                  </span>
                </div>
              </div>
            )}
            {result === 'error' && (
              <div className={style.resultRow}>
                <div className={style.resultError}>
                  <ErrorIcon />
                  <span>
                    {t('verify_result_not_confirmed', 'Contact not confirmed')} – {checkedValue}
                  </span>
                </div>
                <div className={style.resultWarn}>
                  <WarnIcon />
                  <span>
                    {t(
                      'verify_result_check_data',
                      'Please verify that you entered the data correctly'
                    )}
                  </span>
                </div>
              </div>
            )}
            {result === 'unavailable' && (
              <div className={style.resultRow}>
                <div className={style.resultWarn}>
                  <WarnIcon />
                  <span>
                    {t(
                      'verify_result_unavailable',
                      'Service is temporarily unavailable. Please try again later.'
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className={style.helpText}>
            <div>{t('verify_help_not_sure', 'Not sure about the result?')}</div>
            <div>
              {t('verify_help_write_to', 'Write to ')}{' '}
              <Link href="/contacts">{t('verify_help_official_support', 'official support')}</Link>{' '}
              - {t('verify_help_manual_check', 'we will check manually')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
